import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { OutlookLocation, OutlookRecipient } from '../types/resource';

interface AppointmentTime {
  requiredAttendees: OutlookRecipient[];
  optionalAttendees: OutlookRecipient[];
  organizer: string;
  subject: string;
  location: OutlookLocation[];
  enhancedLocation: OutlookLocation[];
  startTime: Date | null;
  endTime: Date | null;
  timeZone: string | null;
  error: string | null;
}

interface OutlookStore extends AppointmentTime {
  // Time setters
  setAppointmentTime: (startTime: Date | null, endTime: Date | null) => void;
  setTimeZone: (timeZone: string | null) => void;
  
  // Attendees setters
  setRequiredAttendees: (attendees: OutlookRecipient[]) => void;
  setOptionalAttendees: (attendees: OutlookRecipient[]) => void;
  setOrganizer: (organizer: string) => void;
  
  // Content setters
  setSubject: (subject: string) => void;
  setLocation: (location: OutlookLocation[]) => void;
  setEnhancedLocation: (enhancedLocation: OutlookLocation[]) => void;
  
  // Utility setters
  setError: (error: string | null) => void;
  clearAll: () => void;
  
  // Getters for debugging
  getAll: () => AppointmentTime;
  
  // Validation methods
  isValid: () => boolean;
  getValidationErrors: () => string[];
  
  // Initialization
  initializeAppointmentTime: () => void;
}

export const useOutlookStore = create<OutlookStore>()(
  devtools(
    (set, get) => ({
  requiredAttendees: [],
  optionalAttendees: [],
  organizer: "",
  subject: "",
  location: [],
  enhancedLocation: [],
  startTime: null,
  endTime: null,
  timeZone: null,
  error: null,

  setTimeZone: (timeZone: string | null) => {
    // console.log("🌐 Store: Setting time zone", timeZone);
    set({ timeZone }, false, "setTimeZone");
  },

  // Time setters
  setAppointmentTime: (startTime, endTime) => {
    // console.log("📅 Store: Setting appointment time", { startTime, endTime });
    set({ startTime, endTime }, false, "setAppointmentTime");
  },

  // Attendees setters
  setRequiredAttendees: (attendees) => {
    // console.log("👥 Store: Setting required attendees", attendees);
    set({ requiredAttendees: attendees }, false, "setRequiredAttendees");
  },
  
  setOptionalAttendees: (attendees) => {
    // console.log("👤 Store: Setting optional attendees", attendees);
    set({ optionalAttendees: attendees }, false, "setOptionalAttendees");
  },
  
  setOrganizer: (organizer) => {
    // console.log("🎯 Store: Setting organizer", organizer);
    set({ organizer }, false, "setOrganizer");
  },

  // Content setters
  setSubject: (subject) => {
    // console.log("📝 Store: Setting subject", subject);
    set({ subject }, false, "setSubject");
  },
  
  setLocation: (location) => {
    console.log("📍 Store: Setting location", location);
    set({ location }, false, "setLocation");
  },
  
  setEnhancedLocation: (enhancedLocation) => {
    console.log("🏢 Store: Setting enhanced location", enhancedLocation);
    set({ enhancedLocation }, false, "setEnhancedLocation");
  },

  // Utility setters
  setError: (error) => {
    // console.log("❌ Store: Setting error", error);
    set({ error }, false, "setError");
  },
  
  clearAll: () => {
    // console.log("🧹 Store: Clearing all appointment data");
    set({
      requiredAttendees: [],
      optionalAttendees: [],
      organizer: "",
      subject: "",
      location: [],
      enhancedLocation: [],
      startTime: null,
      endTime: null,
      timeZone: null,
      error: null,
    }, false, "clearAll");
  },

  // Getters for debugging
  getAll: (): AppointmentTime => {
    const state = get();
    return {
      requiredAttendees: state.requiredAttendees,
      optionalAttendees: state.optionalAttendees,
      organizer: state.organizer,
      subject: state.subject,
      location: state.location,
      enhancedLocation: state.enhancedLocation,
      startTime: state.startTime,
      endTime: state.endTime,
      timeZone: state.timeZone,
      error: state.error,
    };
  },

  // Validation methods
  isValid: (): boolean => {
    const state = get();
    return !!(state.startTime && state.endTime && state.startTime < state.endTime);
  },
  
  getValidationErrors: (): string[] => {
    const state = get();
    const errors: string[] = [];
    
    if (!state.startTime) errors.push("Missing start time");
    if (!state.endTime) errors.push("Missing end time");
    if (state.startTime && state.endTime && state.startTime >= state.endTime) {
      errors.push("End time must be after start time");
    }
    if (state.subject.trim().length === 0) errors.push("Subject is required");
    
    return errors;
  },

  initializeAppointmentTime: async () => {
    try {
      console.log("🚀 Store: Initializing appointment time...");
      
      if (!Office || !Office.context || !Office.context.mailbox || !Office.context.mailbox.item) {
        console.error("❌ Store: Office.js context not available");
        set({ error: "Office.js context not available" }, false, "initializeAppointmentTime/error");
        return;
      }

      const item = Office.context.mailbox.item;
      if (!item || item.itemType !== Office.MailboxEnums.ItemType.Appointment) {
        console.error("❌ Store: Not an appointment item");
        set({ error: "Not an appointment item" }, false, "initializeAppointmentTime/error");
        return;
      }

      const appCompose = item as Office.AppointmentCompose;

      const [startTime, endTime] = await Promise.all([
        new Promise<Date>((resolve, reject) => {
          appCompose.start.getAsync((result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              console.log("✅ Store: Got start time:", result.value);
              resolve(result.value);
            } else {
              console.error("❌ Store: Failed to get start time:", result.error);
              reject(new Error(`Failed to get start time: ${result.error?.message}`));
            }
          });
        }),
        new Promise<Date>((resolve, reject) => {
          appCompose.end.getAsync((result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              console.log("✅ Store: Got end time:", result.value);
              resolve(result.value);
            } else {
              console.error("❌ Store: Failed to get end time:", result.error);
              reject(new Error(`Failed to get end time: ${result.error?.message}`));
            }
          });
        })
      ]);

      console.log("📅 Store: Setting appointment times:", { startTime, endTime });
      set({ startTime, endTime, error: null }, false, "initializeAppointmentTime");
      
    } catch (error) {
      console.error("❌ Store: Failed to initialize appointment time:", error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to get appointment time';
      set({ error: errorMessage }, false, "initializeAppointmentTime/error");
    }
  },
    }),
    {
      name: 'outlook-store', // devtools name
    }
  )
);
